/**
 * 📍 Navigate Page
 * Find facilities in the stadium
 */

import { useNavigate } from 'react-router-dom';
import { User, Ticket, IceCream, LogOut, Accessibility, AlertTriangle, MapPin } from 'lucide-react';
import { useUserStore, useMeetingSpotsStore } from '../store';
import styles from './Navigate.module.css';

// 🚨 Emergency mode - disabled by default (liability concerns)
const SHOW_EMERGENCY_MODE = false;

const navOptions = [
  { id: 'bathroom', icon: User, title: 'Nearest Bathroom', subtitle: '~32m away', color: '#3498DB' },
  { id: 'seat', icon: Ticket, title: 'Back to My Seat', subtitle: 'Set your seat first', color: '#F47920' },
  { id: 'food', icon: IceCream, title: 'Frosty Mango', subtitle: '~47m away', color: '#F9B233' },
  { id: 'exit', icon: LogOut, title: 'Nearest Exit', subtitle: 'Exit 4 - ~28m', color: '#2ECC71' },
  { id: 'accessible', icon: Accessibility, title: 'Accessible Facilities', subtitle: 'Wheelchair routes', color: '#3498DB' },
];

export function NavigatePage() {
  const navigate = useNavigate();
  const seatInfo = useUserStore((state) => state.seatInfo);

  const handleOptionClick = (id: string) => {
    navigate(`/directions?type=${id}`);
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Navigate</h1>
        <p className={styles.subtitle}>Where do you need to go?</p>
      </header>

      <div className={styles.optionsList}>
        {navOptions.map((option) => {
          const Icon = option.icon;
          const subtitle = option.id === 'seat' && seatInfo 
            ? `Section ${seatInfo.section}, Row ${seatInfo.row}`
            : option.subtitle;
          
          return (
            <button
              key={option.id}
              className={styles.optionCard}
              onClick={() => handleOptionClick(option.id)}
            >
              <div 
                className={styles.iconContainer}
                style={{ backgroundColor: `${option.color}15` }}
              >
                <Icon size={28} color={option.color} />
              </div>
              <div className={styles.optionContent}>
                <span className={styles.optionTitle}>{option.title}</span>
                <span className={styles.optionSubtitle}>{subtitle}</span>
              </div>
              <span className={styles.chevron}>›</span>
            </button>
          );
        })}

        {/* Meeting Spots Button */}
        <MeetingSpotsButton navigate={navigate} />

        {/* Emergency Button - hidden by default */}
        {SHOW_EMERGENCY_MODE && (
          <button
            className={styles.emergencyCard}
            onClick={() => navigate('/emergency')}
          >
            <div className={styles.emergencyIcon}>
              <AlertTriangle size={32} />
            </div>
            <div className={styles.optionContent}>
              <span className={styles.emergencyTitle}>Emergency Mode</span>
              <span className={styles.emergencySubtitle}>Get guided to the nearest exit</span>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

// Meeting Spots Button Component
function MeetingSpotsButton({ navigate }: { navigate: (path: string) => void }) {
  const spots = useMeetingSpotsStore((state) => state.spots);
  const spotCount = spots.length;

  return (
    <button
      className={styles.meetingCard}
      onClick={() => navigate('/meeting-spots')}
    >
      <div className={styles.meetingIcon}>
        <MapPin size={28} />
      </div>
      <div className={styles.optionContent}>
        <span className={styles.meetingTitle}>Family Meeting Spots</span>
        <span className={styles.meetingSubtitle}>
          {spotCount === 0
            ? 'Set a spot to meet up'
            : `${spotCount} spot${spotCount > 1 ? 's' : ''} saved`}
        </span>
      </div>
      <span className={styles.chevron}>›</span>
    </button>
  );
}
